import { DashboardUtils } from 'src/app/dashboards/DashboardUtils';
import { ANALYSIS_PROFILES, AnalysisProfile } from '../../dashboard-config/analyses/analysis-profile';
import { Analysis, DashboardConfigurationDependencies, Granularity, Metric } from '../../dashboard-config/dashboard-config.model';
import { composeDatasetName } from '../../dashboard-config/dataset-name';
import { GRANULARITY_PROFILES, GranularityProfile } from '../../dashboard-config/granularities/granularity-profile';
import { KpiColumnContext } from '../../dashboard-config/kpis/kpi-columns';
import { MetricProfile } from '../../dashboard-config/metrics/metric-profile';
import { districtSelections, DistrictSelections, districtYearWindow, YearWindow } from '../../dashboard-config/product-rules';
import { DashboardFilter } from '../../filters/dashboard_filter';
import {
  DashboardMethodologyFromActivatedRouteSingleSelectFilter,
  DashboardMethodologyFromStoreSingleSelectFilter,
} from '../../filters/dashboard_filter_implementations';
import type { CreateDashboardDefinitionBase, DashboardDefinition } from './dashboard-definition.model';

/**
 * Everything a definition derives from its analysis × metric × granularity, resolved once and in
 * plain sight: each field is one expression over the profiles and `ProductRules`. Families build
 * their filters, transformers, headings and view from it and add only their own logic.
 */
export interface DashboardAxes {
  analysis: AnalysisProfile;
  metric: MetricProfile;
  granularity: GranularityProfile;
  dashboardId: string;
  datasetName: string;
  /** Fixed year window at district level (`ProductRules.DISTRICT_YEAR_WINDOWS`). */
  yearWindow?: YearWindow;
  /** Ids preselected at district level (`ProductRules.DISTRICT_SELECTIONS`). */
  selections?: DistrictSelections;
  /** Context for `kpiColumn` overrides. */
  kpiContext: KpiColumnContext;
}

export function resolveDashboardAxes(
  analysisId: Analysis,
  metric: MetricProfile,
  granularityId: Granularity,
  options: {
    /** Granularity of the dataset rows when it differs from the dashboard id's (forecast Bundesvergleich WZ). */
    datasetGranularity?: Granularity;
    /** Analysis segment of the dataset name when it differs per variant (dossiers). */
    datasetAnalysisToken?: string;
  } = {},
): DashboardAxes {
  return {
    analysis: ANALYSIS_PROFILES[analysisId],
    metric,
    granularity: GRANULARITY_PROFILES[granularityId],
    dashboardId: DashboardUtils.encodeDashboardId({ analysisId, metricId: metric.id, granularityId }),
    datasetName: composeDatasetName({
      analysisId,
      metricId: metric.id,
      granularityId: options.datasetGranularity ?? granularityId,
      analysisToken: options.datasetAnalysisToken,
    }),
    yearWindow: districtYearWindow(analysisId, metric.id, granularityId),
    selections: districtSelections(metric.id, granularityId),
    kpiContext: { analysis: analysisId, granularity: granularityId },
  };
}

/** The analysis × granularity a shell profile renders, and the shell identity stamped on its definitions. */
export interface DashboardShellAxes {
  id: string;
  analysisId: Analysis;
  granularityId: Granularity;
  variant?: string;
  /** Granularity of the dataset rows when it differs from the dashboard id's (forecast Bundesvergleich WZ). */
  datasetGranularityId?: Granularity;
  /** Analysis segment of the dataset name when it differs per variant (dossiers: `dssr`, `dssr_sky`). */
  datasetAnalysisToken?: string;
}

/**
 * First line of every definition factory: the shell profile of the input's shell key, the family
 * metric profile, and the axes resolved from both.
 */
export function resolveProduct<
  TShellKey extends string,
  TShell extends DashboardShellAxes,
  TMetric extends { base: MetricProfile },
>(
  input: CreateDashboardDefinitionBase<TShellKey, TMetric>,
  shells: Record<TShellKey, TShell>,
): { axes: DashboardAxes; shell: TShell; metric: TMetric } {
  const shell = shells[input.shell];
  const metric = input.metric;
  const axes = resolveDashboardAxes(shell.analysisId, metric.base, shell.granularityId, {
    datasetGranularity: shell.datasetGranularityId,
    datasetAnalysisToken: shell.datasetAnalysisToken,
  });
  return { axes, shell, metric };
}

type DefinitionIdentity = 'dashboardId' | 'shellId' | 'variant' | 'datasetName' | 'axes';

/** Stamps identity (id, shell, variant, dataset, axes) onto a factory's body. */
export function defineDashboard<TDefinition extends DashboardDefinition<any>>(
  axes: DashboardAxes,
  shell: { id: string; variant?: string },
  body: Omit<TDefinition, DefinitionIdentity>,
): TDefinition {
  return {
    dashboardId: axes.dashboardId,
    shellId: shell.id,
    ...(shell.variant !== undefined ? { variant: shell.variant } : {}),
    datasetName: axes.datasetName,
    axes,
    ...body,
  } as unknown as TDefinition;
}

/** The axes ids views read (charts, popups, slices). */
export function axesView(axes: DashboardAxes): { analysisId: Analysis; metricId: Metric; granularityId: Granularity } {
  return { analysisId: axes.analysis.id, metricId: axes.metric.id, granularityId: axes.granularity.id };
}

/**
 * A heading sentence: the non-empty parts joined by spaces, then `, <time>` when a time is given
 * (`ENTWICKLUNG DER ANZAHL AN … NACH BUNDESLÄNDERN, 2021 - 2023`).
 */
export function composeHeading(parts: readonly unknown[], time?: unknown): string {
  const words = parts.filter((part) => part != null && String(part).trim() !== '').join(' ');
  return time === undefined ? words : `${words}, ${time}`;
}

/** Values are in thousands of euros (revenue, GVA). All KPIs of a metric share their unit. */
export function isKEur(axes: DashboardAxes): boolean {
  return axes.metric.kpis()[0]?.unit === 'kEur';
}

/** Methodology from the route, limited to the product's methodologies (time series, ranking, index). */
export function createRouteMethodologyFilter(deps: DashboardConfigurationDependencies): DashboardFilter<any> {
  return new DashboardMethodologyFromActivatedRouteSingleSelectFilter(
    deps.localDatasetsService,
    deps.store,
    deps.activatedRoute,
    deps.methodologiesLimiterService,
    {},
  );
}

/** Methodology from the store, limited to the given methodologies (forecasts). */
export function createStoreMethodologyFilter(
  deps: DashboardConfigurationDependencies,
  options: { methodologies: readonly string[]; width: string },
): DashboardFilter<any> {
  return new DashboardMethodologyFromStoreSingleSelectFilter(deps.localDatasetsService, deps.store, {
    limitToMethodologies: [...options.methodologies],
    width: options.width,
  });
}
